import React from 'react';
import SEO from '../components/SEO';
import PageHero from '../components/PageHero';
import LegalLayout from '../components/LegalLayout';

const Terms: React.FC = () => {
  return (
    <div>
      <SEO
        title="Terms of Service"
        description="Read the terms and conditions that govern your use of the eTENANT rental property management app and website, provided by INVOVANE COMPANY."
        path="/terms"
        breadcrumb="Terms of Service"
      />

      {/* Page Header */}
      <PageHero
        crumb="Terms & Conditions"
        title={<>Terms & Conditions</>}
        subtitle="Last Updated: 04/04/2026"
      />

      {/* Terms Content */}
      <LegalLayout>

        <div className="terms-section mb-5">
          <h2 className="section-title mb-3">1. Overview</h2>
          <p className="mb-3">
            We are pleased to welcome you to eTENANT.
          </p>
          <p className="mb-3">
            eTENANT provides property and tenant management services for:
          </p>
          <ul className="mb-3">
            <li>Property Owners: Manage your rental properties, tenants, and rental agreements</li>
            <li>Real Estate Agents: Manage leads, multiple properties, property owners, tenants, rental agreements, and sales agreements on behalf of clients</li>
          </ul>
          <p className="mb-3">
            These Terms of Service govern your use of eTENANT' website, located at www.etenant.net.
          </p>
          <p className="mb-3">
            Additionally, our Privacy Policy regulates your use of our Service and describes how we collect, safeguard, and share information about your visits to our platform.
          </p>
          <p className="mb-3">
            Your agreement with us is comprised of these Terms and our Privacy Statement. You acknowledge that you have read and comprehended the Agreements and that you agree to their binding effect.
          </p>
          <p>
            If you do not agree with (or are unable to comply with) the Agreements, you may not use the Service; however, please contact us at <a href="mailto:support@etenant.net" className="text-primary">support@etenant.net</a> so we can work out a solution. These Terms apply to all visitors, users, and those who access or use the Service.
          </p>
        </div>

        <div className="terms-section mb-5">
          <h2 className="section-title mb-3">2. Communications</h2>
          <p className="mb-3">
            We may send you service-related communications (such as account notifications, billing reminders, and security alerts) that are necessary for the operation of your account.
          </p>
          <p>
            Marketing communications (newsletters, promotional materials, and product updates) are only sent if you have explicitly opted in during registration or in your account settings. You may opt out of marketing communications at any time by updating your preferences in My Account or by emailing <a href="mailto:support@etenant.net" className="text-primary">support@etenant.net</a>. Opting out of marketing does not affect service-related communications.
          </p>
        </div>

        <div className="terms-section mb-5">
          <h2 className="section-title mb-3">3. Purchases</h2>
          <p className="mb-3">
            If you desire to purchase any product or service made available through the Service ("Purchase"), you may be requested to provide certain information related to your purchase, such as your credit or debit card number, the expiration date of your card, your billing address, and your shipping information.
          </p>
          <p className="mb-3">
            You represent and warrant that: (I) you are legally entitled to use any card(s) or other payment methods in connection with any Purchase; and (ii) the information you provide to us is true, accurate, and complete.
          </p>
          <p className="mb-3">
            We use Google Play Billing as our payment processor to facilitate the payment for and completion of Purchases. By subscribing to our service, you authorize us to share necessary transaction information with Google Play following our Privacy Policy and Google Play's privacy policies.
          </p>
          <p className="mb-3">
            We reserve the right to refuse or cancel your order at any time for a variety of reasons, including but not limited to product or service availability, typographical or pricing mistakes in the product or service description or price, an error in your order, or other reasons.
          </p>
          <p>
            If we suspect fraud or an unauthorized or illegal transaction, we reserve the right to refuse or cancel your order.
          </p>
        </div>

        <div className="terms-section mb-5">
          <h2 className="section-title mb-3">4. Promotions, Contests, and Sweepstakes</h2>
          <p>
            Any contests, sweepstakes, or other promotions offered through the Service may be governed by rules distinct from these Terms of Service. Please examine the applicable regulations and our Privacy Statement before participating in any Promotions. If a Promotion's terms conflict with these Terms of Service, the Promotion's rules will govern.
          </p>
        </div>

        <div className="terms-section mb-5">
          <h2 className="section-title mb-3">5. Subscription Services</h2>
          <p className="mb-3">
            eTENANT offers the following subscription plans:
          </p>
          <ul className="mb-3">
            <li><strong>Free Plan:</strong> Available to all users at no cost, with limited features and record limits.</li>
            <li><strong>Premium Plan:</strong> A paid plan with expanded limits and additional features, billed monthly or yearly.</li>
            <li><strong>Pro Plan:</strong> A paid plan with higher limits and full access to all features, billed monthly or yearly.</li>
            <li><strong>Customized Plan:</strong> An enterprise plan with custom limits and features, managed directly by the eTENANT team.</li>
          </ul>
          <p className="mb-3">
            New users start on the Free Plan. You may upgrade to the Premium or Pro Plan at any time through the app to unlock additional features and higher limits.
          </p>

          <h4 className="mb-3">Guest Mode:</h4>
          <p className="mb-3">
            You may use eTENANT without creating an account by continuing as a guest. Guest mode provides the same Free Plan features. Guest data is stored on our servers and linked to your anonymous session. If you sign in with Google or register an account, your guest data is automatically migrated and preserved under your new account. If you clear app data without signing in, guest data will be lost. We strongly encourage guest users to sign in with Google to permanently save their data.
          </p>
          <p className="mb-3">
            Plan features and limits are displayed in the application and may vary by account type (Property Owner or Real Estate Agent). Pricing varies by country and is displayed in the application.
          </p>

          <p className="mb-3">
            Paid plans (Premium and Pro) are charged in advance for the selected billing period — either one month ("Monthly Billing") or one year ("Yearly Billing") — and renew automatically unless canceled prior to renewal.
          </p>
          <p className="mb-3">
            You may cancel your subscription or choose not to renew at any time by managing your subscription through the Google Play Store. To cancel your subscription:
          </p>
          <ol className="mb-3">
            <li>Open the Google Play Store app on your device</li>
            <li>Go to Menu &gt; Subscriptions</li>
            <li>Select your eTENANT subscription</li>
            <li>Tap Cancel subscription</li>
          </ol>
          <p className="mb-3">
            You may also contact customer support at <a href="mailto:support@etenant.net" className="text-primary">support@etenant.net</a> for assistance with subscription management.
          </p>
          <p className="mb-3">
            All payments are processed securely through Google Play Billing, which is PCI DSS compliant. You must maintain a valid Google Play account to complete your subscription. By subscribing to a paid plan, you authorize eTENANT and Google Play to collect subscription fees through your Google Play account according to Google Play's terms of service. All subscription management, including billing, renewals, and cancellations, is handled through the Google Play Store.
          </p>
          <p className="mb-3">
            If payment fails for any reason, eTENANT reserves the right to suspend or discontinue your access to your paid plan until the issue is resolved.
          </p>
          <p className="mb-3">
            eTENANT reserves the right to modify subscription plans, pricing, or these Terms of Service at any time without prior notice.
          </p>

          <h4 className="mb-3">Service Limitations and Downgrades:</h4>
          <ul className="mb-3">
            <li>Free Plan users who exceed their limits will be prompted to upgrade or archive excess records</li>
            <li>Downgrading from a paid plan to the Free Plan requires you to reduce records to Free Plan limits within 30 days</li>
            <li>Archived records remain accessible but cannot be edited or used for new agreements</li>
            <li>Exceeding limits will not result in data deletion, but may restrict access to certain features</li>
          </ul>

          <h4 className="mb-3">Ad-Supported Experience:</h4>
          <p className="mb-3">
            Free Plan users may be shown advertisements within the application. By using the Free Plan, you consent to the display of ads. Advertisements are served by third-party ad networks and eTENANT is not responsible for the content of those ads. Upgrading to a paid plan removes advertisements from your experience.
          </p>
        </div>

        <div className="terms-section mb-5">
          <h2 className="section-title mb-3">6. Fee Adjustments and Data Retention</h2>
          <p className="mb-3">
            eTENANT reserves the right, at any time and at its sole discretion, to amend the Subscription fees for the Subscriptions. Any changes to the Subscription charge will take effect after the then-current Billing Cycle.
          </p>
          <p className="mb-3">
            eTENANT shall give you a reasonable prior notice of any change in Subscription rates to allow you to cancel your subscription before the change takes effect.
          </p>
          <p className="mb-3">
            Continued use of the Service following the effective date of the Subscription fee modification signifies your agreement to pay the modified Subscription fee amount.
          </p>

          <h4 className="mb-3">Data Retention:</h4>
          <p className="mb-3">We will retain your data for as long as necessary to fulfill the purposes outlined in our Privacy Policy and these Terms. Specific retention periods include:</p>
          <ul className="mb-3">
            <li>Property, tenant, owner, rental agreement, and sales agreement records: Retained for 10 years after account closure or record deletion (as required by applicable business record-keeping regulations in our operating jurisdictions)</li>
            <li>Payment and financial records: Retained for 10 years for tax, audit, and legal compliance purposes</li>
            <li>Document metadata: Retained for 10 years even if Google Drive is disconnected</li>
            <li>User profile data: Deleted within 30 days of account deletion request, except where retention is required by law</li>
            <li>Business records necessary for legal compliance may be retained beyond account deletion</li>
          </ul>
        </div>

        <div className="terms-section mb-5">
          <h2 className="section-title mb-3">7. Refund Policy</h2>
          <p className="mb-3">
            All subscription payments are processed through Google Play Billing. Refund requests are handled according to Google Play Store refund policies.
          </p>
          <p className="mb-3">To request a refund:</p>
          <ol className="mb-3">
            <li>Open the Google Play Store app</li>
            <li>Go to Menu &gt; Account &gt; Order History</li>
            <li>Select the eTENANT subscription transaction</li>
            <li>Select "Request a refund" and follow the on-screen instructions</li>
            <li>Alternatively, visit play.google.com/store/account/orderhistory</li>
          </ol>
          <p className="mb-3">
            You may also contact our customer support at <a href="mailto:support@etenant.net" className="text-primary">support@etenant.net</a> for assistance.
          </p>

          <h4 className="mb-3">Regional Consumer Rights:</h4>
          <ul className="mb-3">
            <li><strong>European Union / United Kingdom:</strong> Under the Consumer Rights Directive, you have a 14-day cooling-off period from the date of purchase during which you may cancel your subscription for a full refund. If you have consented to begin the service immediately and acknowledged that you lose your right of withdrawal once the service is fully performed, the cooling-off period may be reduced proportionally.</li>
            <li><strong>Australia:</strong> Under Australian Consumer Law, you are entitled to a refund if the service has a major failure, is not fit for purpose, or does not match what was described. These statutory rights cannot be excluded.</li>
            <li><strong>New Zealand:</strong> Under the Consumer Guarantees Act 1993, you have similar rights to a remedy if services fail to meet statutory guarantees.</li>
            <li><strong>Other jurisdictions:</strong> Your local consumer protection laws may provide additional refund rights that cannot be overridden by these terms.</li>
          </ul>
          <p>
            In all cases, nothing in this policy limits your statutory consumer rights.
          </p>
        </div>

        <div className="terms-section mb-5">
          <h2 className="section-title mb-3">8. Content</h2>
          <p className="mb-3">
            Our Service enables you to publish, link to, store, share, and otherwise make available certain information, text, graphics, videos, and other material (collectively referred to as "Content"). You are entirely responsible for the Content that you upload to or transmit via the Service, including its legality, reliability, and appropriateness.
          </p>
          <p className="mb-3">
            You agree to indemnify, defend, and hold harmless eTENANT, its affiliates, officers, directors, employees, and agents from and against any claims, damages, losses, liabilities, and all costs and expenses (including attorneys' fees) arising from or relating to any Content you upload, publish, or share via the Service, including but not limited to copyright infringement or violation of any third-party rights.
          </p>
          <p className="mb-3">
            By posting Content on or through the Service, you represent and warrant that: (I) the Content is yours (you own it) and/or you have the right to use it and to grant us the rights and licenses outlined in these Terms; and (ii) the posting of your Content on or through the Service does not violate any third party's privacy, publicity, copyright, contract, or other rights. We reserve the right to suspend or permanently terminate the accounts of users who are found to be repeat infringers of copyright or other intellectual property rights, in accordance with the DMCA and applicable law.
          </p>
          <p className="mb-3">
            You maintain complete ownership of all rights in and to any Content you submit, post, or display on or through the Service, and you are solely responsible for safeguarding those rights. We take no responsibility and assume no liability for any Content posted on or via the Service by you or any third party. By posting Content on or via the Service, you hereby grant us the right and license to use, edit, publicly perform, publicly display, reproduce, and distribute such Content on and via the Service. You acknowledge and agree that this license includes the right for us to make your Content available to other Service users, who may likewise use it following these Terms.
          </p>
          <p className="mb-3">
            eTENANT reserves the right but is not obligated, to monitor and edit all Content submitted by users.
          </p>
          <p>
            Additionally, the Content on or accessible via this Service is the property of eTENANT or is utilized with permission. Without our express prior written consent, you may not distribute, edit, transmit, reuse, download, repost, copy, or use said Content, in whole or in part, for commercial reasons or personal gain.
          </p>
        </div>

        <div className="terms-section mb-5">
          <h2 className="section-title mb-3">9. Copyrighted Content and Reporting</h2>
          <p className="mb-3">
            Users are strictly prohibited from uploading, sharing, or distributing copyrighted images, files, or any other content without proper authorization. By uploading content, you affirm that you have the legal right to use and share it.
          </p>
          <p className="mb-3">
            If you believe that any content on eTENANT infringes on your copyright, you may report it to us for immediate review and removal. Please send an email to <a href="mailto:support@etenant.net" className="text-primary">support@etenant.net</a> with the following details:
          </p>
          <ul className="mb-3">
            <li>A description of the copyrighted work that you claim has been infringed.</li>
            <li>The URL or specific location of the infringing material on our platform.</li>
            <li>Your contact information (name, email, and phone number).</li>
            <li>A statement that you have a good faith belief that the use of the material is unauthorized.</li>
            <li>A declaration that the information you provide is accurate and that you are the copyright owner or authorized to act on behalf of the owner.</li>
          </ul>
          <p className="mb-3">
            Upon verification, we will promptly remove the reported content and may take further action against repeat offenders.
          </p>

          <h4 className="mb-3">Property Image Copyright Enforcement:</h4>
          <ul className="mb-3">
            <li>Property images uploaded to eTENANT must not infringe on photographer copyrights, property owner rights, or other intellectual property</li>
            <li>Professional photographs require explicit permission from the photographer or proof of ownership/license</li>
            <li>Stock photos must be properly licensed for commercial use with documentation available upon request</li>
            <li>Images containing recognizable artwork, branded items, or copyrighted materials require appropriate permissions</li>
            <li>Real estate agents must have written authorization from property owners to upload and publish property images</li>
          </ul>

          <h4 className="mb-3">Property Owner Rights:</h4>
          <ul className="mb-3">
            <li>Property owners retain ownership rights to their properties and may request removal of images at any time</li>
            <li>Agents must comply with property owner removal requests within 24 hours</li>
            <li>eTENANT will facilitate image removal requests from verified property owners</li>
          </ul>

          <h4 className="mb-3">Photographer Rights:</h4>
          <ul className="mb-3">
            <li>Professional photographers retain copyright to their photos unless explicitly transferred in writing</li>
            <li>Property owners or agents must obtain usage rights before uploading professional photos</li>
            <li>We respect photographer intellectual property and will enforce takedown requests</li>
            <li>Photographers may claim copyright by providing original files, metadata, or copyright registration</li>
          </ul>

          <h4 className="mb-3">Image Takedown Process for Copyright Holders:</h4>
          <p className="mb-3">To request removal of infringing images, email <a href="mailto:support@etenant.net" className="text-primary">support@etenant.net</a> with:</p>
          <ul className="mb-3">
            <li>Proof of copyright ownership:
              <ul>
                <li>Photographer: Original RAW files, copyright registration, or photography contract</li>
                <li>Property Owner: Property deed, title documents, or authorization letter</li>
              </ul>
            </li>
            <li>Specific URLs or listing IDs containing infringing images</li>
            <li>Your contact information (name, email, phone)</li>
            <li>A statement under penalty of perjury that you are the copyright holder or authorized to act on their behalf</li>
            <li>A declaration that the information provided is accurate</li>
          </ul>

          <h4 className="mb-3">Response Timeline:</h4>
          <ul className="mb-3">
            <li>Emergency takedowns (clearly infringing content): Within 24 hours</li>
            <li>Standard takedown requests: Within 48-72 hours</li>
            <li>Complex copyright disputes: Within 5-7 business days (may require legal review)</li>
            <li>Removed images will be permanently deleted from our storage</li>
            <li>We will notify the uploading user of the takedown (unless prohibited by law or court order)</li>
          </ul>

          <h4 className="mb-3">Repeat Infringer Policy:</h4>
          <p className="mb-3">Users who upload infringing content repeatedly will face:</p>
          <ul className="mb-3">
            <li>First violation: Warning and mandatory removal of infringing images</li>
            <li>Second violation: Account warning, 30-day upload suspension, and required copyright compliance training</li>
            <li>Third violation: Permanent account termination and prohibition from creating new accounts</li>
            <li>We maintain records of copyright violations for all users</li>
            <li>Serious violations (malicious intent, commercial piracy) may result in immediate account termination and legal action</li>
          </ul>
        </div>

        <div className="terms-section mb-5">
          <h2 className="section-title mb-3">10. Restricted Uses</h2>
          <p className="mb-3">
            You may use the Service only in line with the Terms and for legitimate reasons. You agree to refrain from using the Service:
          </p>
          <ul className="mb-3">
            <li>0.1. In any manner that is inconsistent with applicable domestic or international law or regulation.</li>
            <li>0.2. To exploit, injure, or attempting to exploit or harm minors in any way, including by the exposure of children to unsuitable content.</li>
            <li>0.3. To communicate or acquire the transmission of any commercial or promotional material, including but not limited to "junk mail," "chain letters," "spam," or any other similar solicitation.</li>
            <li>0.4. To impersonate or attempt to impersonate the Company, an employee of the Company, another user, or any other individual or entity.</li>
            <li>0.5. In any connection that violates the rights of others, or in any manner that is illegal, threatening, fraudulent, or harmful, or in any manner that is connected with any unlawful, illegal, fraudulent, or harmful purpose or action.</li>
            <li>0.6. To engage in any other behavior that limits or inhibits another person's use or enjoyment of the Service, or that, in our opinion, may harm or offend the Company or other users of the Service, or subject them to liability.</li>
          </ul>
          <p className="mb-3">
            Additionally, you agree to refrain from the following:
          </p>
          <ul className="mb-3">
            <li>0.1. Use the Service in any way that could disable, overburden, damage, or impair the Service, or interfere with any other party's use of the Service, including their ability to engage in real-time activities through the Service.</li>
            <li>0.2. Access the Service by the use of a robot, spider, or other automatic devices, method, or methods for any purpose, including monitoring or duplicating any of the material on the Service.</li>
            <li>0.3. Without our prior written authorization, use any manual process to monitor or copy any of the Content on the Service or for any other unlawful purpose.</li>
            <li>0.4. Use any equipment, program, or procedure that impairs the Service's proper operation.</li>
            <li>0.5. Introduce any dangerous or technologically dangerous viruses, trojan horses, worms, logic bombs, or other material.</li>
            <li>0.6. Make an unauthorized attempt to gain access to, interfere with, damage, or disrupt any portion of the Service, the server on which the Service is housed, or any server, computer, or database connected to the Service.</li>
            <li>0.7. Launch a denial-of-service attack or a distributed denial-of-service attack against the Service.</li>
            <li>0.8. Take any activity that could jeopardize or distort the Company's rating.</li>
            <li>0.9. Otherwise, seek to obstruct the Service's proper operation.</li>
          </ul>
        </div>

        <div className="terms-section mb-5">
          <h2 className="section-title mb-3">11. Analytical services</h2>
          <p>
            Third-party Service Providers may be used to monitor and evaluate how our Service is used.
          </p>
        </div>

        <div className="terms-section mb-5">
          <h2 className="section-title mb-3">12. Unsuitable for Minors</h2>
          <p>
            The service is designed for those who are at least eighteen (18) years old to access and use. By accessing or using the Service, you warrant and represent that you are at least eighteen (18) years old and possess the entire authorization, right, and capacity to engage in this agreement and adhere to all of the Terms' terms and conditions. If you are under the age of eighteen (18) years, you are not permitted to access or use the Service.
          </p>
        </div>

        <div className="terms-section mb-5">
          <h2 className="section-title mb-3">13. Account</h2>
          <p className="mb-3">
            By creating an account with us, you represent that you are at least 18 years old and that the information you submit is true, complete, and current at all times. Incomplete, inaccurate, or out-of-date information may result in the immediate termination of your Service account.
          </p>
          <p className="mb-3">
            eTENANT uses Google Sign-In as its authentication method. By signing in with Google, you authorize eTENANT to access your Google account's basic profile information, including your name, email address, and profile photo, solely for the purpose of creating and maintaining your eTENANT account. We do not access any other Google account data beyond what is necessary for authentication. Your use of Google Sign-In is also subject to Google's Terms of Service and Privacy Policy.
          </p>
          <p className="mb-3">
            You are responsible for safeguarding access to your Google account, which includes limiting access to your device and/or account. You agree to assume responsibility for any or all activities or actions conducted using your account. You must promptly contact us if you become aware of a security breach or unauthorized use of your account.
          </p>
          <p className="mb-3">
            Without prior authorization, you may not use the Google account of another person or entity during sign-in.
          </p>
          <p>
            We reserve the right, at our sole discretion, to refuse service, terminate accounts, remove or edit content, or cancel orders.
          </p>
        </div>

        <div className="terms-section mb-5">
          <h2 className="section-title mb-3">14. Intellectual Property</h2>
          <p className="mb-3">
            eTENANT and its licensors retain sole ownership of the Service and its unique content (excluding Content submitted by users), features, and functionality.
          </p>
          <h4 className="mb-3">14.1 Platform Disclaimer</h4>
          <p>
            eTENANT is a neutral platform that enables users to upload, share, and manage property-related content. We do not create, edit, or endorse user-submitted content. All responsibility for ensuring the legality and originality of such content lies solely with the user.
          </p>
        </div>

        <div className="terms-section mb-5">
          <h2 className="section-title mb-3">15. DMCA and Copyright Policy</h2>
          <p className="mb-3">
            We are mindful of others' intellectual property rights. It is our policy to respond to any accusation that Content posted on the Service violates a person's or entity's copyright or other intellectual property rights ("Infringement").
          </p>
          <p className="mb-3">
            If you are a copyright owner, or an authorized representative of one, and believe that a copyrighted work has been copied in a manner that constitutes copyright infringement, please send an email to <a href="mailto:support@etenant.net" className="text-primary">support@etenant.net</a> with the subject line "Copyright Infringement" and include a detailed description of the alleged Infringement as detailed below under "DMCA Notice and Procedure."
          </p>
          <p className="mb-3">
            You may be held liable for damages (including costs and attorneys' fees) resulting from misrepresentation or bad-faith allegations regarding the violation of your copyright by any Content found on and/or via the Service.
          </p>
          <p className="mb-3">
            eTENANT is an internet service provider under 17 U.S.C. § 512 and claims safe harbor protection under the Digital Millennium Copyright Act (DMCA). As such, eTENANT is not liable for user-uploaded content provided that we promptly remove infringing material when properly notified under the DMCA. We do not prescreen or endorse any content submitted by users.
          </p>

          <h4 className="mb-3">DMCA Safe Harbor and Service Provider Status:</h4>
          <p className="mb-3">eTENANT qualifies as an internet service provider under 17 U.S.C. § 512 and claims safe harbor protection under the Digital Millennium Copyright Act (DMCA). As such:</p>
          <ul className="mb-3">
            <li>We are not liable for user-uploaded content provided we promptly remove infringing material when properly notified</li>
            <li>We do not pre-screen, monitor, or editorially control user-uploaded images</li>
            <li>We do not have actual knowledge of infringing content until notified</li>
            <li>We implement a repeat infringer policy and terminate accounts of repeat offenders</li>
            <li>We expeditiously remove or disable access to allegedly infringing material upon receiving valid DMCA notices</li>
          </ul>

          <h4 className="mb-3">Designated Copyright Agent:</h4>
          <p className="mb-3">Our DMCA Copyright Agent for takedown notices is:</p>
          <p className="mb-3">Email: <a href="mailto:support@etenant.net" className="text-primary">support@etenant.net</a></p>
          <p className="mb-3">Subject Line: "DMCA Copyright Takedown Notice"</p>

          <h4 className="mb-3">Counter-Notification Process:</h4>
          <p className="mb-3">If you believe content was removed in error, you may file a DMCA counter-notification:</p>
          <ul className="mb-3">
            <li>Send email to <a href="mailto:support@etenant.net" className="text-primary">support@etenant.net</a> with subject "DMCA Counter-Notification"</li>
            <li>Include:
              <ul>
                <li>Your full legal name and contact information (address, phone, email)</li>
                <li>Description of the removed content and where it was located</li>
                <li>Statement under penalty of perjury that removal was erroneous or mistaken identity</li>
                <li>Statement that you consent to the jurisdiction of the courts in the Philippines or a court of competent jurisdiction in your country of residence</li>
                <li>Statement that you will accept service of process from the complaining party</li>
                <li>Your physical or electronic signature</li>
              </ul>
            </li>
            <li>We will forward your counter-notification to the complaining party within 10 business days</li>
            <li>If the complaining party does not file legal action within 10-14 business days, we may restore your content</li>
            <li>False counter-notifications may result in legal liability for damages</li>
          </ul>

          <h4 className="mb-3">Repeat Infringer Account Termination:</h4>
          <ul className="mb-3">
            <li>We maintain a database of copyright infringement complaints</li>
            <li>Users with 3 or more valid DMCA complaints will have accounts permanently terminated</li>
            <li>Terminated users are prohibited from creating new accounts</li>
            <li>We may share repeat infringer information with other platforms or legal authorities as required by law</li>
            <li>Account termination decisions may be appealed with legal justification to <a href="mailto:support@etenant.net" className="text-primary">support@etenant.net</a></li>
          </ul>
        </div>

        <div className="terms-section mb-5">
          <h2 className="section-title mb-3">16. DMCA Notice and Procedure for Allegations of Copyright Infringement</h2>
          <p className="mb-3">
            You may file notification under the Digital Millennium Copyright Act (DMCA) by giving the following information in writing to our Copyright Agent (for more information, see 17 U.S.C. 512(c)(3)):
          </p>
          <ul className="mb-3">
            <li>0.1. the electronic or physical signature of the individual authorized to act on behalf of the copyright holder;</li>
            <li>0.2. a description of the infringed copyrighted work, including the URL (i.e., web page address) of the location of the allegedly infringed copyrighted work or a copy of the allegedly infringed copyrighted work;</li>
            <li>0.3. the URL or other precise location on the Service where the allegedly infringing item is located;</li>
            <li>0.4. your physical mailing address, telephone number, and email address;</li>
            <li>0.5. a statement by you stating you believe the disputed use is not authorized by the copyright owner, its agent, or the law;</li>
            <li>0.6. a declaration by you, made under penalty of perjury, that the information contained in your notice is accurate and that you are the copyright owner or authorized to act on behalf of the copyright owner.</li>
          </ul>
          <p>
            Our Copyright Agent can be reached via email at <a href="mailto:support@etenant.net" className="text-primary">support@etenant.net</a>.
          </p>
        </div>

        <div className="terms-section mb-5">
          <h2 className="section-title mb-3">17. Reporting and Feedback on Errors</h2>
          <p>
            You may give us information and feedback about errors, suggestions for improvements, ideas, problems, complaints, and other things relating to our Service ("Feedback") either directly at <a href="mailto:support@etenant.net" className="text-primary">support@etenant.net</a> or via third-party sites and platforms. You acknowledge and agree that (i) you will not retain, acquire, or assert any intellectual property or other rights, title, or interest in or to the Feedback; (ii) Company may have development ideas similar to the Feedback; (iii) the Feedback does not contain any confidential or proprietary information from you or any third party; and (iv) Company is not under any obligation of confidence. If you are unable to transfer ownership of the Feedback due to applicable mandatory laws, you grant Company and its affiliates an exclusive, transferable, irrevocable, royalty-free, sub-licensable, unlimited, and perpetual right to use the Feedback in any manner and for any purpose (including copying, modifying, creating derivative works, publishing, distributing, and commercializing).
          </p>
        </div>

        <div className="terms-section mb-5">
          <h2 className="section-title mb-3">18. Hypertext Links to Related Websites</h2>
          <p className="mb-3">
            Our Service may contain links to third-party websites or services that are not affiliated with, controlled by, or maintained by eTENANT.
          </p>
          <p className="mb-3">
            eTENANT does not control and is not responsible for the content, privacy policies, or practices of any third-party websites or services. We make no representations or warranties about these entities/individuals or their websites.
          </p>
          <p className="mb-3">
            YOU AGREE THAT THE COMPANY SHALL NOT BE RESPONSIBLE OR LIABLE, DIRECTLY OR INDIRECTLY, FOR ANY DAMAGE OR LOSS CAUSED OR ALLEGED TO BE CAUSED BY OR IN CONNECTION WITH THE USE OR RELIANCE ON ANY SUCH CONTENT, GOODS, OR SERVICES AVAILABLE ON OR THROUGH ANY SUCH THIRD-PARTY WEB SITES OR SERVICES.
          </p>
          <p>
            WE STRONGLY RECOMMEND THAT YOU READ THE TERMS OF SERVICE AND PRIVACY POLICIES OF ANY THIRD-PARTY WEBSITES OR SERVICES THAT YOU VISIT.
          </p>
        </div>

        <div className="terms-section mb-5">
          <h2 className="section-title mb-3">19. Warranty Disclaimer</h2>
          <p className="mb-3">
            THE COMPANY PROVIDES THESE SERVICES ON AN "AS IS" AND "AS AVAILABLE" BASIS. THE COMPANY MAKES NO REPRESENTATIONS OR WARRANTIES, EXPRESS OR IMPLIED, REGARDING THE OPERATION OF THEIR SERVICES OR THE INFORMATION, CONTENT, OR MATERIALS CONTAINED THEREIN. YOU AGREE EXPRESSLY THAT THE USE OF THESE SERVICES, THEIR CONTENT, AND ANY SERVICES OR ITEMS OBTAINED FROM US IS AT YOUR SOLE RISK.
          </p>
          <p className="mb-3">
            NEITHER THE COMPANY NOR ANYONE ASSOCIATED WITH IT MAKES ANY WARRANTY OR REPRESENTATION REGARDING THE COMPLETENESS, SECURITY, RELIABILITY, QUALITY, ACCURACY, OR AVAILABILITY OF THE SERVICES. INCLUDING BUT NOT LIMITED TO THE FOREGOING, NEITHER COMPANY NOR ANYONE ASSOCIATED WITH THE COMPANY REPRESENTS OR WARRANTS THAT THE SERVICES, THEIR CONTENT, OR ANY SERVICES OR ITEMS OBTAINED THROUGH THE SERVICES WILL BE ACCURATE, RELIABLE, ERROR-FREE, OR UNINTERRUPTED, THAT DEFECTS WILL BE CORRECTED, THAT THE SERVICES OR THE SERVER ON WHICH THE SERVICES ARE MADE AVAILABLE ARE FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS.
          </p>
          <p className="mb-3">
            THE COMPANY HEREBY DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, STATUTORY OR OTHERWISE, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, NON-INFRINGEMENT, AND FITNESS FOR A PARTICULAR PURPOSE.
          </p>
          <p>
            THE FOREGOING IS WITHOUT LIMITATION ON ANY WARRANTIES THAT CANNOT BE EXCLUDED OR LIMITED BY APPLICABLE LAW.
          </p>
        </div>

        <div className="terms-section mb-5">
          <h2 className="section-title mb-3">20. Liability Limitation</h2>
          <p>
            EXCEPT AS PROHIBITED BY LAW, YOU WILL INDEMNIFY US AND OUR OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS FROM ANY INDIRECT, PUNITIVE, SPECIAL, INCIDENTAL, OR CONSEQUENTIAL DAMAGE, HOWEVER IT ARIES (INCLUDING ATTORNEYS' FEES AND ALL RELATED COSTS AND EXPENSES OF LITIGATION AND ARBITRATION, EXCEPT AS REQUIRED BY LAW, COMPANY'S LIABILITY WILL BE LIMITED TO THE AMOUNT PAID FOR THE PRODUCTS AND/OR SERVICES, AND UNDER NO CIRCUMSTANCES WILL CONSEQUENTIAL OR PUNITIVE DAMAGES BE AWARDED. SOME STATES DO NOT ALLOW FOR THE EXCLUSION OR LIMITATION OF PUNITIVE, INCIDENTAL, OR CONSEQUENTIAL DAMAGES, AND AS A RESULT, THE ABOVE LIMITATION OR EXCLUSION MAY NOT APPLY TO YOU.
          </p>
        </div>

        <div className="terms-section mb-5">
          <h2 className="section-title mb-3">21. Termination and Account Deletion</h2>
          <p className="mb-3">
            We reserve the right to instantly cancel or suspend your account and deny access to the Service, without prior warning or liability, for any cause, and without restriction, including but not limited to a breach of the Terms.
          </p>
          <p className="mb-3">
            You may terminate your account by simply ceasing to use the Service or by requesting account deletion.
          </p>

          <h4 className="mb-3">Account Deletion Process:</h4>
          <ul className="mb-3">
            <li>You may request complete account deletion by contacting <a href="mailto:support@etenant.net" className="text-primary">support@etenant.net</a></li>
            <li>Upon deletion request, we will remove your personal information within 30 days</li>
            <li>Business records (property, tenant, payment data) may be retained for 10 years as required by applicable law</li>
            <li>Subscription cancellation through Google Play does not automatically delete your account</li>
            <li>Deleted accounts cannot be recovered</li>
            <li>You must cancel your subscription separately through Google Play before requesting account deletion</li>
            <li>Important: Deleting your account within the app does not automatically cancel your Google Play subscription. If you have an active subscription, you must manually cancel it through the Google Play Store (Menu &gt; Subscriptions &gt; eTENANT &gt; Cancel) to avoid future charges. eTENANT is not responsible for any charges incurred after account deletion if the Google Play subscription was not cancelled separately.</li>
          </ul>

          <p>
            All sections of the Terms that, by their nature, should survive termination shall do so, including, without limitation, ownership provisions, warranty disclaimers, indemnification, and liability limits.
          </p>
        </div>

        <div className="terms-section mb-5">
          <h2 className="section-title mb-3">22. Governing Legislation</h2>
          <p className="mb-3">
            These Terms shall be governed and construed following the laws of the Philippines, where INVOVANE COMPANY is registered. However, nothing in these Terms shall deprive you of the protection afforded by mandatory consumer protection laws in your country of residence. If you reside in the European Union, United Kingdom, Australia, Canada, or any other jurisdiction with mandatory consumer protection laws, those local laws shall apply to the extent they provide greater protection than Philippine law.
          </p>
          <p className="mb-3">
            For disputes, you may bring claims in the courts of your country of residence if required by local consumer protection law, or in the courts of the Philippines.
          </p>
          <p>
            Failure by us to enforce any right or term of these Terms will not be construed as a waiver of those rights or provisions. If a court determines that any provision of these Terms is unlawful or unenforceable, the remaining sections of these Terms will remain in effect. These Terms comprise the complete agreement between us concerning our Service and supersede and replace all prior agreements between us concerning our Service.
          </p>
        </div>

        <div className="terms-section mb-5">
          <h2 className="section-title mb-3">23. Service Modifications</h2>
          <p>
            We reserve the right, at our sole discretion, to remove or alter our Service, as well as any service or material we provide via the Service, without notice. We will not be liable if all or any portion of the Service is unavailable at any time or for any period for any reason. We reserve the time to restrict access to certain portions of the Service, or the entire Service, to users, including registered users, at any time.
          </p>
        </div>

        <div className="terms-section mb-5">
          <h2 className="section-title mb-3">24. Modifications to Terms</h2>
          <p className="mb-3">
            We reserve the right to modify these Terms at any time by posting the modified terms on this site. You have to frequently check these Terms.
          </p>
          <p className="mb-3">
            By continuing to use the Platform following the publishing of amended Terms, you agree that you have read, understood, and agreed to the changes. You are responsible for routinely checking this page to ensure that you are informed of any changes, as they are binding on you.
          </p>
          <p>
            By continuing to access or use our Service following the effective date of any amendments, you agree to the revised terms. If you do not agree to the revised terms, you will be unable to use the Service.
          </p>
        </div>

        <div className="terms-section mb-5">
          <h2 className="section-title mb-3">25. Severability and Waiver</h2>
          <p className="mb-3">
            Company's waiver of any term or condition outlined in Terms shall not be construed as a further or continuing waiver of such term or condition or any other term or condition, and Company's failure to assert a right or provision under Terms shall not be construed as a waiver of such right or provision.
          </p>
          <p>
            If any term of these Terms is found to be invalid, illegal, or unenforceable for any reason, that provision shall be removed or limited to the smallest extent necessary to ensure that the remaining sections of these Terms remain in full force and effect.
          </p>
        </div>

        <div className="terms-section mb-5">
          <h2 className="section-title mb-3">26. Phone Contacts Permission</h2>
          <p className="mb-3"><strong>This permission is optional and enhances your user experience.</strong></p>
          <p className="mb-3">
            By using eTENANT, you may be asked to grant permission to access your device's phone contacts. This permission is entirely optional and is used solely to simplify the process of adding contact information (tenants, property owners, leads, etc.).
          </p>

          <h4 className="mb-3">Purpose of Contacts Access:</h4>
          <ul className="mb-3">
            <li>To allow you to import contact information when adding new records</li>
            <li>To auto-fill contact details (name, phone, email) from your selected contact</li>
            <li>To reduce manual data entry and minimize errors</li>
          </ul>

          <h4 className="mb-3">User Consent and Control:</h4>
          <ul className="mb-3">
            <li>You will be shown a disclosure explaining why we need this permission before it is requested</li>
            <li>You can choose to grant or deny this permission at any time</li>
            <li>Granting permission does not automatically upload or share your contacts</li>
            <li>You manually select which specific contact to import each time</li>
            <li>You can revoke this permission through your device settings at any time</li>
          </ul>

          <h4 className="mb-3">What We Do NOT Do:</h4>
          <ul className="mb-3">
            <li>We do NOT access your contacts without your explicit action to import</li>
            <li>We do NOT upload or store your entire contacts list</li>
            <li>We do NOT share contact information with third parties</li>
            <li>We do NOT use contacts for any purpose other than pre-filling forms</li>
            <li>We do NOT access contacts in the background or automatically</li>
          </ul>

          <h4 className="mb-3">Data Usage:</h4>
          <ul className="mb-3">
            <li>Only the contact you specifically select is used to populate the form</li>
            <li>The imported information becomes part of the record you create</li>
            <li>No separate database of your contacts is created or maintained</li>
            <li>Standard data policies apply to the imported information</li>
          </ul>

          <h4 className="mb-3">Your Rights:</h4>
          <ul className="mb-3">
            <li>You may choose to manually enter information instead of importing from contacts</li>
            <li>Denying contacts permission does not limit any core functionality of the app</li>
            <li>You can delete any records created from imported contacts at any time</li>
            <li>Revoking permission will not affect existing records</li>
          </ul>

          <h4 className="mb-3">Compliance:</h4>
          <ul className="mb-3">
            <li>We handle contacts data in accordance with Google Play's User Data policy</li>
            <li>Contacts access complies with applicable privacy regulations (GDPR, CCPA, and other applicable data protection laws)</li>
            <li>We provide prominent disclosure before requesting permission</li>
            <li>Permission is requested only when you initiate the import feature</li>
          </ul>
        </div>

        <div className="terms-section mb-5">
          <h2 className="section-title mb-3">27. Google Drive Integration</h2>
          <p className="mb-3"><strong>Google Drive document management requires a paid subscription.</strong></p>
          <p className="mb-3">To use document management features, you must:</p>
          <ul className="mb-3">
            <li>Have a valid Google account</li>
            <li>Authorize eTENANT to access your Google Drive</li>
            <li>Grant permissions for eTENANT to create, read, and delete files in app-specific folders</li>
          </ul>

          <h4 className="mb-3">eTENANT Access Permissions:</h4>
          <ul className="mb-3">
            <li>Limited to files created by eTENANT app only</li>
            <li>Cannot access other files in your Google Drive</li>
            <li>Uses OAuth 2.0 secure authentication</li>
            <li>You can revoke access anytime via Google Account settings</li>
          </ul>

          <h4 className="mb-3">User Responsibilities:</h4>
          <ul className="mb-3">
            <li>Maintain sufficient Google Drive storage space</li>
            <li>Ensure your Google account remains active</li>
            <li>Comply with Google Drive's Terms of Service</li>
            <li>You own and control all uploaded documents</li>
            <li>You are responsible for document backups</li>
          </ul>

          <h4 className="mb-3">eTENANT is NOT Responsible for:</h4>
          <ul className="mb-3">
            <li>Google Drive service outages or downtime</li>
            <li>Loss of data due to Google Drive issues</li>
            <li>Your Google account suspension or termination</li>
            <li>Documents deleted from your Google Drive outside of eTENANT app</li>
          </ul>

          <h4 className="mb-3">Document Deletion:</h4>
          <ul className="mb-3">
            <li>Deleting documents in eTENANT also deletes them from your Google Drive</li>
            <li>Deletion is permanent and cannot be undone by eTENANT</li>
            <li>Google Drive trash/recovery policies apply</li>
          </ul>

          <h4 className="mb-3">Disconnecting Google Drive:</h4>
          <ul className="mb-3">
            <li>You may disconnect eTENANT from Google Drive at any time</li>
            <li>Disconnecting will make documents inaccessible via eTENANT</li>
            <li>Documents remain in your Google Drive after disconnection</li>
            <li>Document metadata remains in eTENANT for record-keeping (10 years - as required by applicable law)</li>
          </ul>
        </div>

        <div className="terms-section mb-5">
          <h2 className="section-title mb-3">28. Services for Real Estate Agents</h2>
          <p className="mb-3">When using eTENANT as a Real Estate Agent, you acknowledge additional responsibilities:</p>

          <h4 className="mb-3">a) Client Data Management</h4>
          <ul className="mb-3">
            <li>You are responsible for obtaining proper consent from property owners and tenants before entering their information into eTENANT</li>
            <li>You must comply with applicable real estate licensing laws in your jurisdiction</li>
            <li>You represent that you have authority to manage properties and client data on behalf of property owners</li>
            <li>All data entered by you is your responsibility and must comply with applicable privacy laws</li>
          </ul>

          <h4 className="mb-3">b) Lead Management</h4>
          <ul className="mb-3">
            <li>Leads you create are your responsibility to manage in compliance with applicable marketing and privacy laws</li>
            <li>You must obtain consent before converting leads to tenants or owners</li>
            <li>Lead data remains your property but must comply with our data protection standards</li>
            <li>You are responsible for maintaining accurate and up-to-date lead information</li>
          </ul>

          <h4 className="mb-3">c) Commission Tracking</h4>
          <ul className="mb-3">
            <li>Commission data is for your internal record-keeping only</li>
            <li>eTENANT is not responsible for commission disputes between you and property owners</li>
            <li>You are solely responsible for tax reporting and legal compliance related to commissions</li>
            <li>Commission calculations and tracking are your responsibility</li>
          </ul>

          <h4 className="mb-3">d) Data Separation and Confidentiality</h4>
          <ul className="mb-3">
            <li>You acknowledge that data for different clients/properties is logically separated within your account</li>
            <li>You must not share one client's data with another without explicit permission</li>
            <li>You are responsible for maintaining confidentiality of client information</li>
            <li>You must comply with professional ethical standards for real estate agents in your jurisdiction</li>
          </ul>
        </div>

        <div className="terms-section mb-5">
          <h2 className="section-title mb-3">29. Data Sharing and Access Rights</h2>

          <h4 className="mb-3">a) Property Owner Access</h4>
          <ul className="mb-3">
            <li>Property owners whose properties are managed by agents may request access to their property data</li>
            <li>Agents must facilitate such requests or provide data exports within 30 days</li>
            <li>Owners retain rights to their property information even when managed by agents</li>
            <li>eTENANT may provide property data directly to verified owners upon request</li>
          </ul>

          <h4 className="mb-3">b) Tenant Information</h4>
          <ul className="mb-3">
            <li>Tenants do not have direct access to eTENANT unless invited by property owner or agent</li>
            <li>Tenants may request copies of their personal data at any time by contacting <a href="mailto:support@etenant.net" className="text-primary">support@etenant.net</a></li>
            <li>Property owners and agents must respond to tenant data requests within 30 days</li>
            <li>Tenant personal data must be handled in compliance with applicable privacy laws</li>
          </ul>

          <h4 className="mb-3">c) Agent-Owner Relationships</h4>
          <ul className="mb-3">
            <li>When an agent-owner relationship ends, the agent must transfer or delete owner's property data as requested</li>
            <li>eTENANT may facilitate data transfer between accounts upon mutual agreement of both parties</li>
            <li>Agents must provide departing clients with complete copies of their data within 30 days</li>
            <li>Property owners may request eTENANT to restrict agent access to their data</li>
          </ul>

          <h4 className="mb-3">d) Communication Preferences</h4>
          <ul className="mb-3">
            <li>Communication platform preferences (WhatsApp, Viber, Messenger, etc.) are stored for record-keeping purposes only</li>
            <li>eTENANT does not directly contact your tenants, owners, or leads through these platforms</li>
            <li>You (the user) are responsible for all communications with your contacts</li>
            <li>Paid subscription users may receive automated rent and recurring charge (e.g. utilities, other charges) due reminders via email and push notification through eTENANT</li>
            <li>Pro and Customized plan users based in the Philippines may additionally receive automated rent and recurring charge due reminders via SMS sent to the tenant's registered phone number</li>
            <li>SMS due reminders are currently supported for Globe/TM subscribers only; support for other Philippine networks (Smart/TNT, DITO) is in progress and delivery to those networks is not yet guaranteed</li>
            <li>If you enable SMS due reminders, you are responsible for notifying your tenants that they will receive these SMS reminders and for obtaining their consent to receive them</li>
          </ul>
        </div>

        <div className="terms-section mb-5">
          <h2 className="section-title mb-3">30. Acknowledgement</h2>
          <p>
            BY USING THE SERVICE OR OTHER SERVICES PROVIDED BY US, YOU ACKNOWLEDGE THAT YOU HAVE READ AND AGREE TO BE BOUND BY THESE TERMS OF SERVICE.
          </p>
        </div>

        <div className="terms-section mb-5">
          <h2 className="section-title mb-3">31. Contact Us</h2>
          <p>
            Kindly send us feedback, comments, and requests for technical support to <a href="mailto:support@etenant.net" className="text-primary">support@etenant.net</a> through email.
          </p>
        </div>

        <div className="terms-section mb-5">
          <h2 className="section-title mb-3">32. Property Image Upload Terms (For Property Owners and Real Estate Agents)</h2>
          <p className="mb-3">When uploading property images to eTENANT, you acknowledge and agree to the following:</p>

          <h4 className="mb-3">a) User Eligibility for Uploads</h4>
          <ul className="mb-3">
            <li>Only registered Property Owners and Real Estate Agents may upload property images</li>
            <li>Public users, tenants, and guests cannot upload content</li>
            <li>You must maintain an active paid subscription to access upload features</li>
            <li>Account verification may be required for large-scale uploads</li>
          </ul>

          <h4 className="mb-3">b) Copyright and Ownership</h4>
          <ul className="mb-3">
            <li>You represent and warrant that you own all rights to the images you upload, OR you have obtained all necessary permissions, licenses, and consents to upload these images</li>
            <li>Real Estate Agents must obtain written authorization from property owners before uploading images of their properties</li>
            <li>You must not upload images that infringe on any third party's copyright, trademark, privacy rights, or other intellectual property rights</li>
            <li>Professional photographer images require explicit written permission or proof of license</li>
            <li>By uploading images, you grant eTENANT a non-exclusive, worldwide, royalty-free license to store, reproduce, and process these images in connection with the Service</li>
          </ul>

          <h4 className="mb-3">c) Agent-Specific Requirements</h4>
          <p className="mb-3">If you are a Real Estate Agent:</p>
          <ul className="mb-3">
            <li>You must obtain written authorization from property owners to photograph images of their properties</li>
            <li>You must maintain records of these authorizations for as long as the images are stored plus 10 years</li>
            <li>You represent that you have the legal authority to act on behalf of property owners</li>
            <li>You agree to indemnify eTENANT against claims from property owners regarding unauthorized image use</li>
            <li>Property owners may request removal of their property images at any time</li>
          </ul>

          <h4 className="mb-3">d) Image Metadata and Privacy Protection</h4>
          <p className="mb-3">eTENANT automatically strips EXIF metadata from all uploaded images, including:</p>
          <ul className="mb-3">
            <li>GPS location coordinates</li>
            <li>Camera make, model, and settings</li>
            <li>Original creation timestamps</li>
            <li>Photographer copyright information embedded in metadata</li>
            <li>Software used to edit images</li>
          </ul>
          <p className="mb-3">This protects your privacy and the privacy of property owners</p>
          <p className="mb-3">You should still review images before upload to ensure no sensitive information is visible in the image itself (e.g., documents, personal belongings, identifiable people)</p>
          <p className="mb-3">We recommend blurring license plates, house numbers, or other identifying information if privacy is a concern</p>

          <h4 className="mb-3">e) Prohibited Content</h4>
          <p className="mb-3">You must not upload images containing:</p>
          <ul className="mb-3">
            <li>Copyrighted material without proper authorization or license</li>
            <li>People's faces or identifiable information without their signed consent or model releases</li>
            <li>Minors (children under 18) without parental consent</li>
            <li>Inappropriate, offensive, obscene, or illegal content</li>
            <li>Misleading or deceptive representations of properties</li>
            <li>Images from other property listing platforms or competitors</li>
            <li>Watermarks, logos, or branding from other real estate agencies without permission</li>
            <li>Professional MLS (Multiple Listing Service) photos without photographer authorization</li>
            <li>Stock photography without proper commercial use licensing</li>
          </ul>

          <h4 className="mb-3">f) Professional Photography</h4>
          <p className="mb-3">If using professional photographs, you must have:</p>
          <ul className="mb-3">
            <li>Written permission from the photographer, OR</li>
            <li>Proof of purchase/license for commercial use, OR</li>
            <li>Transfer of copyright ownership in writing</li>
          </ul>
          <p className="mb-3">Real estate agents cannot use photographers' work without explicit authorization</p>
          <p className="mb-3">Photography contracts should specify usage rights for your intended use</p>
          <p className="mb-3">Keep copies of all photography agreements and licenses</p>

          <h4 className="mb-3">g) Your Responsibilities</h4>
          <ul className="mb-3">
            <li>You are solely responsible for ensuring you have the legal right to upload all images</li>
            <li>You must obtain necessary consents from photographers, property owners, tenants, current occupants, or other rights holders</li>
            <li>You agree to indemnify, defend, and hold harmless eTENANT from any claims, damages, losses, or expenses (including attorneys' fees) arising from your image uploads</li>
            <li>If you are an agent, you represent that you have written authorization from property owners</li>
            <li>You must remove images immediately upon request from copyright holders or property owners</li>
          </ul>

          <h4 className="mb-3">h) eTENANT's Rights and Responsibilities</h4>
          <ul className="mb-3">
            <li>We reserve the right to remove any images that violate these terms, applicable law, or our policies</li>
            <li>We may remove images in response to copyright complaints, takedown requests, or court orders</li>
            <li>We are not obligated to monitor, pre-screen, or moderate uploaded images</li>
            <li>We claim DMCA safe harbor protection as an internet service provider under 17 U.S.C. § 512</li>
            <li>We do not endorse or guarantee the accuracy, legality, or quality of user-uploaded images</li>
            <li>We may implement automated content filtering, moderation systems, or AI-based detection in the future</li>
          </ul>

          <h4 className="mb-3">i) Image Storage and Retention</h4>
          <ul className="mb-3">
            <li>Images are stored on Supabase Cloud Storage infrastructure (third-party provider)</li>
            <li>Images remain stored while your account and the associated property are active</li>
            <li>Deleted images are permanently removed from storage within 24-48 hours</li>
            <li>Image metadata records may be retained for 10 years per applicable data protection law requirements (actual files deleted per above schedule)</li>
            <li>Account deletion results in permanent image deletion within 30 days</li>
          </ul>

          <h4 className="mb-3">j) Copyright Takedown Requests</h4>
          <p className="mb-3">Property owners, photographers, or authorized copyright holders may request image removal</p>
          <p className="mb-3">Send requests to <a href="mailto:support@etenant.net" className="text-primary">support@etenant.net</a> with:</p>
          <ul className="mb-3">
            <li>Proof of copyright ownership or property ownership</li>
            <li>Specific property or image identifiers</li>
            <li>Your contact information and legal representation (if applicable)</li>
          </ul>
          <p className="mb-3">We will review and respond to legitimate requests within 48-72 hours</p>
          <p className="mb-3">Removed images will be permanently removed from storage</p>
          <p className="mb-3">We will notify the uploading user of takedown requests (unless prohibited by law)</p>

          <h4 className="mb-3">k) Consequences of Violations</h4>
          <ul className="mb-3">
            <li>First Violation: Warning and required removal of infringing images</li>
            <li>Second Violation: Account warning, temporary upload restrictions, and mandatory copyright training</li>
            <li>Third Violation: Permanent account termination and ban from creating new accounts</li>
          </ul>
          <p className="mb-3">We maintain records of copyright violations and will cooperate with legal authorities in investigating repeated infringement.</p>
        </div>

        <div className="terms-section mb-5">
          <h2 className="section-title mb-3">33. Public Listings Terms (For Tenants, Guests, and Public Viewers)</h2>
          <p className="mb-3">eTENANT provides a public listings feature that allows unauthenticated users to browse published property listings.</p>

          <h4 className="mb-3">a) Public Access and Usage</h4>
          <ul className="mb-3">
            <li>Property listings are publicly accessible without requiring login or account creation</li>
            <li>You may browse listings, view property images, and access property information freely</li>
            <li>You must create an account and login to contact property owners or real estate agents</li>
            <li>Public listings are provided "as-is" for informational purposes only</li>
          </ul>

          <h4 className="mb-3">b) View-Only Access</h4>
          <ul className="mb-3">
            <li>Public users, tenants, and guests may ONLY VIEW listings</li>
            <li>You cannot upload properties, images, or any content without creating an account</li>
            <li>Account creation requires user type selection (Property Owner or Real Estate Agent)</li>
            <li>Tenant accounts may be added in future versions</li>
          </ul>

          <h4 className="mb-3">c) Information Displayed in Public Listings</h4>
          <p className="mb-3">Public listings show:</p>
          <ul className="mb-3">
            <li>Property images uploaded by owners/agents</li>
            <li>Property address and location</li>
            <li>Property type, features, and amenities (bedrooms, bathrooms, size, etc.)</li>
            <li>Listing description provided by owner/agent</li>
            <li>Pricing information (daily or monthly rent, or sale price)</li>
            <li>General area and neighborhood information</li>
          </ul>

          <h4 className="mb-3">d) Information NOT Displayed Publicly</h4>
          <ul className="mb-3">
            <li>Owner or agent personal contact information (email, phone, mobile number)</li>
            <li>Owner or agent full names or personal details</li>
            <li>Payment or financial transaction information</li>
            <li>Private notes or internal property management data</li>
            <li>Communication preferences or messaging history</li>
            <li>Other properties managed by the same owner/agent (unless also published)</li>
          </ul>

          <h4 className="mb-3">e) Contact and Inquiry Process</h4>
          <ul className="mb-3">
            <li>You must create an account and login to contact property owners or agents</li>
            <li>When logged in, you can initiate contact through the platform</li>
            <li>We may share your name, email, and inquiry message with the property owner/agent</li>
            <li>Owners/agents control whether to respond to your inquiries</li>
            <li>We are not responsible for the quality, accuracy, or timeliness of owner/agent responses</li>
          </ul>

          <h4 className="mb-3">f) Content Accuracy and Liability</h4>
          <ul className="mb-3">
            <li>eTENANT does not verify the accuracy of property listings or uploaded images</li>
            <li>Listings are created and managed by property owners and real estate agents</li>
            <li>We are not responsible for misleading, inaccurate, or outdated listing information</li>
            <li>You should verify all property details directly with owners/agents before making decisions</li>
            <li>We are not parties to any rental agreements or sales transactions between users</li>
            <li>All transactions and agreements are between you and the property owner/agent</li>
          </ul>

          <h4 className="mb-3">g) Copyright and Image Usage</h4>
          <ul className="mb-3">
            <li>Property images displayed in public listings are owned by property owners, real estate agents, or their licensors</li>
            <li>You may view images for personal, non-commercial property search purposes only</li>
            <li>You must NOT download, copy, reproduce, distribute, or republish property images without explicit written permission from the copyright holder</li>
            <li>Screenshots or copies of listings for personal reference are allowed</li>
            <li>Commercial use (e.g., republishing on other platforms, using in marketing materials) is strictly prohibited without permission</li>
          </ul>

          <h4 className="mb-3">h) Reporting Inappropriate Content</h4>
          <p className="mb-3">You may report listings that contain:</p>
          <ul className="mb-3">
            <li>Copyright infringement (stolen images)</li>
            <li>Inappropriate or offensive content</li>
            <li>Misleading or deceptive information</li>
            <li>Privacy violations (identifiable people without consent)</li>
            <li>Fraud or scam attempts</li>
            <li>Other violations of our Terms or applicable law</li>
          </ul>
          <p className="mb-3">Use the "Report this listing" button in the app or email <a href="mailto:support@etenant.net" className="text-primary">support@etenant.net</a></p>
          <p className="mb-3">Provide specific details, URLs, and reasons for your report</p>
          <p className="mb-3">We will investigate reports within 24-48 hours and take appropriate action</p>
          <p className="mb-3">False or malicious reports may result in reporting restrictions</p>

          <h4 className="mb-3">i) Privacy and Data Collection from Public Viewers</h4>
          <p className="mb-3">When you browse public listings without logging in, we collect:</p>
          <ul className="mb-3">
            <li>IP address and general location (country/city level for analytics)</li>
            <li>Device type and browser information</li>
            <li>Pages viewed and time spent on listings (anonymous analytics)</li>
          </ul>
          <p className="mb-3">We do NOT collect personal information unless you create an account</p>
          <p className="mb-3">We use cookies for analytics and functionality (see our Privacy Policy)</p>
          <p className="mb-3">You may disable cookies in your browser settings</p>

          <h4 className="mb-3">j) Search Engine Indexing</h4>
          <ul className="mb-3">
            <li>Public listings may be indexed by search engines (Google, Bing, etc.)</li>
            <li>Listings may appear in search results with images and property information</li>
            <li>Property owners/agents consent to this indexing when publishing listings</li>
            <li>Search engines may cache or archive listings independently</li>
            <li>We cannot control how search engines display or store listing data</li>
          </ul>

          <h4 className="mb-3">k) Third-Party Sharing</h4>
          <ul className="mb-3">
            <li>You may share listing URLs on social media or messaging platforms</li>
            <li>Direct links to public listings are allowed and expected</li>
            <li>Shared links will remain active while the listing is published</li>
            <li>Property owners/agents may unpublish listings at any time, breaking shared links</li>
            <li>We are not responsible for links shared by users to external platforms</li>
          </ul>

          <h4 className="mb-3">l) Geographic Availability</h4>
          <ul className="mb-3">
            <li>eTENANT operates internationally and supports property management in multiple countries</li>
            <li>Listings may be for properties in any supported country</li>
            <li>Users should verify local legal requirements for property transactions in their jurisdiction</li>
            <li>Currency is displayed based on the user's country setting</li>
            <li>Feature availability may vary by region</li>
          </ul>

          <h4 className="mb-3">m) Age Restrictions</h4>
          <ul className="mb-3">
            <li>You must be at least 18 years old to use eTENANT</li>
            <li>Minors may browse listings with parental supervision</li>
            <li>Account creation and contact initiation require users to be 18+</li>
            <li>We do not knowingly collect personal information from children under 18</li>
          </ul>
        </div>

        <div className="terms-section mb-5">
          <h2 className="section-title mb-3">34. AI-Generated Agreement Draft Feature</h2>
          <p className="mb-3">eTENANT provides an Agreement Draft feature that uses artificial intelligence to generate rental agreement templates. By using this feature, you acknowledge and agree to the following terms.</p>

          <h4 className="mb-3">a) Nature of Agreement Drafts</h4>
          <ul className="mb-3">
            <li>Agreement drafts generated by eTENANT are TEMPLATES FOR REVIEW PURPOSES ONLY</li>
            <li>They do not constitute legally binding contracts or legal documents</li>
            <li>They do not constitute legal advice of any kind</li>
            <li>eTENANT is not a law firm and does not provide legal services</li>
            <li>The drafts are starting points intended to assist users in preparing their own agreements</li>
          </ul>

          <h4 className="mb-3">b) No Legal Advice</h4>
          <ul className="mb-3">
            <li>Nothing produced by the Agreement Draft feature should be construed as legal advice</li>
            <li>eTENANT, its employees, and its AI service providers are not acting as your legal counsel</li>
            <li>The feature does not create an attorney-client relationship between you and eTENANT</li>
            <li>You should always consult a qualified legal professional licensed in your jurisdiction before using any agreement as a binding contract</li>
          </ul>

          <h4 className="mb-3">c) User Responsibility</h4>
          <ul className="mb-3">
            <li>You are solely responsible for reviewing, modifying, and verifying any generated draft before use</li>
            <li>You are responsible for ensuring the final agreement complies with the laws and regulations applicable in your country, state, or province</li>
            <li>You must not use an AI-generated draft as a legally binding contract without independent legal review</li>
            <li>Any agreement you execute based on a draft generated by this feature is your sole responsibility</li>
          </ul>

          <h4 className="mb-3">d) AI Limitations and Accuracy</h4>
          <ul className="mb-3">
            <li>AI-generated content may contain errors, omissions, inaccuracies, or outdated legal language</li>
            <li>The AI model may not be aware of recent changes to laws or regulations in your jurisdiction</li>
            <li>Generated drafts are based on general rental agreement principles and may not reflect the specific legal requirements of your locality</li>
            <li>eTENANT makes no representations or warranties regarding the legal sufficiency, accuracy, or completeness of any generated draft</li>
          </ul>

          <h4 className="mb-3">e) Jurisdiction and Applicable Law</h4>
          <ul className="mb-3">
            <li>Rental and tenancy laws vary significantly by country, state, and municipality</li>
            <li>A draft that is appropriate in one jurisdiction may be non-compliant in another</li>
            <li>eTENANT does not guarantee that any generated draft complies with the laws of your jurisdiction</li>
            <li>Users operating in regulated jurisdictions must independently verify compliance with local tenancy legislation</li>
          </ul>

          <h4 className="mb-3">f) Third-Party AI Services</h4>
          <ul className="mb-3">
            <li>The Agreement Draft feature uses OpenAI's language model API to generate content</li>
            <li>Your rental agreement details (property information, tenant details, agreement terms, and any additional clauses you provide) are transmitted to OpenAI for processing</li>
            <li>OpenAI's use of this data is governed by OpenAI's Terms of Use and Privacy Policy</li>
            <li>By using this feature, you consent to the transmission of this information to OpenAI</li>
            <li>eTENANT does not use your draft content to train AI models</li>
          </ul>

          <h4 className="mb-3">g) Content Safety and Acceptable Use</h4>
          <ul className="mb-3">
            <li>You must only use the Additional Clause field to input content relevant to rental agreements</li>
            <li>Attempting to manipulate or bypass the AI system through prompt injection or misuse is strictly prohibited</li>
            <li>eTENANT reserves the right to suspend or terminate access to this feature for users who misuse it</li>
            <li>You must not use this feature to generate content that is unlawful, discriminatory, or violates the rights of any party</li>
          </ul>

          <h4 className="mb-3">h) Data Storage</h4>
          <ul className="mb-3">
            <li>Generated agreement drafts are stored in your account and can be deleted by you at any time</li>
            <li>Deleted drafts are soft-deleted and may be retained for a limited period for data integrity purposes before permanent deletion</li>
            <li>Draft content is not shared with other users or third parties except as required to deliver the service</li>
          </ul>

          <h4 className="mb-3">i) Limitation of Liability</h4>
          <ul className="mb-3">
            <li>eTENANT is not liable for any loss, damage, dispute, legal claim, or financial consequence arising from your use or reliance on any AI-generated agreement draft</li>
            <li>eTENANT expressly disclaims all warranties, express or implied, regarding the legal validity, enforceability, or suitability of any generated draft</li>
            <li>Your use of this feature is entirely at your own risk</li>
            <li>To the maximum extent permitted by applicable law, eTENANT's liability in connection with this feature is limited to the amount you paid for the subscription in the month the issue arose</li>
          </ul>

          <h4 className="mb-3">j) Feature Availability</h4>
          <ul className="mb-3">
            <li>Free Plan users receive 1 complimentary AI draft generation</li>
            <li>Premium Plan users receive 10 AI draft generations per month, resetting at the start of each billing cycle</li>
            <li>Unused generations do not carry over to the following month</li>
            <li>eTENANT reserves the right to modify generation limits at any time with reasonable notice</li>
          </ul>
        </div>

        <div className="terms-section mb-5">
          <h2 className="section-title mb-3">35. Rental Assistant Feature (Home Dashboard)</h2>
          <p>
            The "Rental Assistant" feature on the Home screen is a separate, non-AI dashboard tool that only summarizes your own property, tenant, and payment data already stored in your account, and does not transmit any information to OpenAI or any other third party.
          </p>
        </div>

      </LegalLayout>
    </div>
  );
};

export default Terms;
